import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { MessageEvent } from 'pubnub';
import { usePubNub } from 'pubnub-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  createOrGetInquiryChannel,
  getInquiryContacts,
  updateInquirySeenTime,
  type TInquiryContact,
} from '../../../api/inquiryChat';
import { Conversation } from '../../../components/Conversation';
import { Loading } from '../../../components/Loading';
import { Text } from '../../../components/Text';
import {
  commonStrings,
  inquiryChatStrings,
  sidebarStrings,
} from '../../../constants/strings';
import { useCurrentUser } from '../../../hooks';
import { useMe } from '../../../hooks/useMe';
import { useOrganizationScopeOrNull } from '../../../components/OrganizationScope/context';
import { isOtherOrganizationPersonalChannel } from '../../../utils/personalChannels';
import { InquiryContactListItem } from './InquiryContactListItem';
import {
  getTrustedRecallMessageNotification,
  isIncomingChannelNotification,
  isMessageRecalledByPubNub,
} from '../../../utils/chatRecallMessage';
import { fetchAndApplyRecallStatusForLastMessages } from '../../../utils/fetchRecallStatusForLastMessages';

// ローディング中に data が undefined になっても contacts の参照を一定に保つ。
// 毎レンダー新しい [] を作ると channelIds memo・未読初期化 effect が再発火し続けてループする
const EMPTY_CONTACTS: TInquiryContact[] = [];

export default function InquiryChatContent() {
  const pubnub = usePubNub();
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const { data: me } = useMe();
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [openingMemberId, setOpeningMemberId] = useState<number | null>(null);
  const channelId = searchParams.get('channel');
  const memberIdParam = searchParams.get('member');

  const {
    data: contacts = EMPTY_CONTACTS,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['inquiry-chat-contacts'],
    queryFn: getInquiryContacts,
    staleTime: 1000,
  });

  const [lastMessageList, setLastMessageList] = useState<
    Record<
      string,
      {
        channel: string;
        message: unknown;
        timetoken: string | number;
        uuid?: string;
        recalled?: boolean;
      }
    >
  >({});

  const [unreadMessageList, setUnreadMessageList] = useState<
    Record<string, number>
  >({});

  const updateUnread = useCallback(
    (chId: string, reset: boolean) => {
      setUnreadMessageList((prev) => ({
        ...prev,
        [chId]: reset
          ? 0
          : (prev[chId] ??
              contacts.find((c) => c.channelId === chId)?.unreadCount ??
              0) + 1,
      }));
    },
    [contacts],
  );

  const clearUnreadLocally = useCallback((chId: string) => {
    setUnreadMessageList((prev) => ({ ...prev, [chId]: 0 }));
  }, []);

  const syncSeenToServer = useCallback(
    (chId: string) => {
      void updateInquirySeenTime(chId, organizationId).then(() =>
        queryClient.invalidateQueries({ queryKey: ['inquiry-chat-contacts'] }),
      );
    },
    [queryClient, organizationId],
  );

  const getUnreadCount = useCallback(
    (contact: TInquiryContact): number => {
      const chId = contact.channelId;
      if (!chId) return 0;

      if (channelId === chId) {
        return 0;
      }

      const lastMessage = lastMessageList[chId];
      if (lastMessage?.uuid === currentUser?.uuid) {
        return 0;
      }

      const local = unreadMessageList[chId];
      if (local !== undefined) {
        return local;
      }

      return contact.unreadCount ?? 0;
    },
    [
      channelId,
      currentUser?.uuid,
      lastMessageList,
      unreadMessageList,
    ],
  );

  useEffect(() => {
    setUnreadMessageList((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const contact of contacts) {
        if (contact.channelId && next[contact.channelId] === undefined) {
          next[contact.channelId] = contact.unreadCount;
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [contacts]);

  const filteredContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;

    return contacts.filter((c) => c.name.toLowerCase().includes(q));
  }, [contacts, search]);

  const sortedContacts = useMemo(() => {
    return [...filteredContacts].sort((a, b) => {
      const aToken = a.channelId
        ? lastMessageList[a.channelId]?.timetoken
        : undefined;
      const bToken = b.channelId
        ? lastMessageList[b.channelId]?.timetoken
        : undefined;
      const aTime = aToken
        ? Number(aToken) / 10000
        : a.lastMessageTime
          ? moment(a.lastMessageTime).valueOf()
          : 0;
      const bTime = bToken
        ? Number(bToken) / 10000
        : b.lastMessageTime
          ? moment(b.lastMessageTime).valueOf()
          : 0;

      return bTime - aTime;
    });
  }, [filteredContacts, lastMessageList]);

  const channelIds = useMemo(
    () => contacts.map((c) => c.channelId).filter((id): id is string => !!id),
    [contacts],
  );
  const userNotificationChannel = currentUser?.uuid;
  const pubnubSubscriptionChannels = useMemo(() => {
    if (!userNotificationChannel) return channelIds;
    return channelIds.includes(userNotificationChannel)
      ? channelIds
      : [...channelIds, userNotificationChannel];
  }, [channelIds, userNotificationChannel]);

  const refreshLastMessage = useCallback(
    async (targetChannelId: string) => {
      if (!pubnub) return;

      try {
        const res = await pubnub.fetchMessages({
          channels: [targetChannelId],
          count: 1,
          includeMessageActions: true,
        });
        const messages = res.channels[targetChannelId];

        if (messages?.length) {
          const lastMsg = messages[0];
          setLastMessageList((prev) => ({
            ...prev,
            [targetChannelId]: {
              channel: targetChannelId,
              message: lastMsg.message,
              timetoken: lastMsg.timetoken,
              uuid: lastMsg.uuid,
              recalled: isMessageRecalledByPubNub(lastMsg.actions),
            },
          }));
          return;
        }

        setLastMessageList((prev) => {
          const next = { ...prev };
          delete next[targetChannelId];
          return next;
        });
      } catch (err) {
        console.error('Error refreshing last message after delete:', err);
      }
    },
    [pubnub],
  );

  // Refresh the contact list preview/unread after a recall. The server unread
  // already excludes recalled messages, so drop the optimistic local override
  // (the contacts effect re-seeds it) and refetch. Shared by the trusted
  // RECALL_MESSAGE PubNub handler and the local confirm handler so the sidebar
  // updates immediately instead of only after a page refresh.
  const handleRecallRefresh = useCallback(
    (targetChannelId: string) => {
      void refreshLastMessage(targetChannelId);
      setUnreadMessageList((prev) => {
        if (prev[targetChannelId] === undefined) return prev;
        const next = { ...prev };
        delete next[targetChannelId];
        return next;
      });
      void queryClient.invalidateQueries({
        queryKey: ['inquiry-chat-contacts'],
      });
    },
    [refreshLastMessage, queryClient],
  );

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // AppLayout は全所属の個人チャネルを同じクライアントで購読するため、別組織の通知では連絡先を取り直さない
      if (
        isOtherOrganizationPersonalChannel(
          me?.memberships,
          event.channel,
          organizationId,
        )
      ) {
        return;
      }
      const recallNotification = getTrustedRecallMessageNotification(
        event.message,
        event.publisher,
      );
      if (recallNotification?.channel) {
        handleRecallRefresh(recallNotification.channel);
        return;
      }

      if (!isIncomingChannelNotification(event.message)) return;

      const chId = event.message.channel;
      const isMyMessage = currentUser?.uuid === event.publisher;

      setLastMessageList((prev) => ({
        ...prev,
        [chId]: {
          channel: chId,
          message: event.message,
          timetoken: event.timetoken,
          uuid: event.publisher,
        },
      }));

      if (isMyMessage) {
        updateUnread(chId, true);
        if (channelId === chId) {
          clearUnreadLocally(chId);
          syncSeenToServer(chId);
        }
        return;
      }

      if (chId === channelId) {
        clearUnreadLocally(chId);
        syncSeenToServer(chId);
        return;
      }

      updateUnread(chId, false);
      void queryClient.invalidateQueries({
        queryKey: ['inquiry-chat-contacts'],
      });
    },
    [
      channelId,
      clearUnreadLocally,
      currentUser?.uuid,
      queryClient,
      syncSeenToServer,
      updateUnread,
      handleRecallRefresh,
      me?.memberships,
      organizationId,
    ],
  );

  const handleMessageRef = useRef(handleMessage);
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  const channelIdsRef = useRef(channelIds);
  const pubnubSubscriptionChannelsRef = useRef(pubnubSubscriptionChannels);
  channelIdsRef.current = channelIds;
  pubnubSubscriptionChannelsRef.current = pubnubSubscriptionChannels;
  const channelIdsKey = channelIds.join(',');
  const pubnubSubscriptionChannelsKey = pubnubSubscriptionChannels.join(',');

  useEffect(() => {
    const currentChannelIds = channelIdsRef.current;
    const currentSubscriptionChannels = pubnubSubscriptionChannelsRef.current;
    if (!pubnub || !currentSubscriptionChannels.length) return;

    const listener = {
      message: (event: MessageEvent) => handleMessageRef.current(event),
    };

    pubnub.subscribe({ channels: currentSubscriptionChannels });
    pubnub.addListener(listener);

    return () => {
      if (currentChannelIds.length > 0) {
        pubnub.unsubscribe({ channels: currentChannelIds });
      }
      pubnub.removeListener(listener);
    };
  }, [pubnub, pubnubSubscriptionChannelsKey, channelIdsKey]);

  useEffect(() => {
    if (!pubnub || !channelIds.length) return;

    const fetchLastMessages = async () => {
      try {
        // NOTE: includeMessageActions is unsupported when fetching multiple
        // channels at once — PubNub throws "History can return actions data for a
        // single channel only." Requesting it here makes the whole call reject and
        // leaves every channel's preview blank on (re)load. Recall state for the
        // list is handled live via RECALL_MESSAGE events and the single-channel
        // refreshLastMessage instead.
        const res = await pubnub.fetchMessages({
          channels: channelIds,
          count: 1,
        });

        const newState: typeof lastMessageList = {};
        Object.keys(res.channels).forEach((chId) => {
          const messages = res.channels[chId];
          if (messages?.length) {
            const lastMsg = messages[0];
            newState[chId] = {
              channel: chId,
              message: lastMsg.message,
              timetoken: lastMsg.timetoken,
              uuid: lastMsg.uuid,
              // Bulk fetch cannot include message actions; recall labels are applied below via backend status.
              recalled: false,
            };
          }
        });

        setLastMessageList((prev) => ({ ...prev, ...newState }));

        await fetchAndApplyRecallStatusForLastMessages(
          newState,
          setLastMessageList,
        );
      } catch (err) {
        console.error('Error fetching last messages:', err);
      }
    };

    void fetchLastMessages();
  }, [pubnub, channelIds]);

  const openChannelMutation = useMutation({
    mutationFn: (targetMemberId: number) =>
      createOrGetInquiryChannel(targetMemberId),
    onSuccess: ({ channelId: newChannelId }, targetMemberId) => {
      setSearchParams({
        channel: newChannelId,
        member: String(targetMemberId),
      });
      void refetch();
    },
    onSettled: () => setOpeningMemberId(null),
  });

  const handleSelectContact = useCallback(
    async (contact: TInquiryContact) => {
      if (contact.channelId) {
        const chId = contact.channelId;
        const hadUnread =
          channelId !== chId &&
          (unreadMessageList[chId] ?? contact.unreadCount ?? 0) > 0;

        clearUnreadLocally(chId);
        setSearchParams({
          channel: chId,
          member: String(contact.memberId),
        });

        if (hadUnread || (contact.unreadCount ?? 0) > 0) {
          syncSeenToServer(chId);
        }
        return;
      }

      setOpeningMemberId(contact.memberId);
      openChannelMutation.mutate(contact.memberId);
    },
    [
      channelId,
      clearUnreadLocally,
      openChannelMutation,
      setSearchParams,
      syncSeenToServer,
      unreadMessageList,
    ],
  );

  useEffect(() => {
    if (!memberIdParam || channelId || isLoading) return;
    const memberId = Number(memberIdParam);
    const contact = contacts.find((c) => c.memberId === memberId);
    if (contact) {
      void handleSelectContact(contact);
    }
  }, [memberIdParam, channelId, contacts, isLoading, handleSelectContact]);

  useEffect(() => {
    if (!channelId) return;
    clearUnreadLocally(channelId);
    syncSeenToServer(channelId);
  }, [channelId, clearUnreadLocally, syncSeenToServer]);

  // モバイルの会話画面から一覧へ戻る。
  // member パラメータも一緒に除去しないと自動再選択の effect で会話に引き戻される
  const onBackToList = useCallback(() => {
    if (!channelId) return;
    clearUnreadLocally(channelId);
    // 戻る時もサーバへ既読同期する。開いた直後の syncSeenToServer が in flight の
    // まま離脱するとサーバに未読が残り UI（0表示）と乖離するため、ChatContent の
    // onBackToList と同様に再同期して総未読も再取得する。
    syncSeenToServer(channelId);
    setSearchParams({});
  }, [channelId, clearUnreadLocally, syncSeenToServer, setSearchParams]);

  return (
    <section className="chat-section-h chat-fullbleed rounded-none border-0 lg:rounded-[28px] lg:border border-white/70 bg-white/85 shadow-soft backdrop-blur-sm overflow-hidden">
      <div className="h-full flex">
        <div
          className={`${
            channelId ? 'hidden md:flex' : 'flex'
          } h-full basis-full md:basis-2/5 lg:basis-1/4 md:border-r border-neutral-200 flex-col bg-transparent`}
        >
          <div className="flex-shrink-0 px-4 py-3 border-b border-neutral-200">
            <h2 className="text-sm font-semibold text-ink mb-2">
              {sidebarStrings.navLabels.inquiryChat}
            </h2>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={commonStrings.search}
              className="w-full rounded-xl border border-border/70 px-3 py-2 text-base lg:text-sm"
            />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loading />
              </div>
            ) : sortedContacts.length === 0 ? (
              <Text className="p-4 text-muted text-sm" center>
                {commonStrings.noData}
              </Text>
            ) : (
              sortedContacts.map((contact) => {
                const isActive =
                  channelId === contact.channelId ||
                  memberIdParam === String(contact.memberId);

                return (
                  <InquiryContactListItem
                    key={contact.memberId}
                    contact={contact}
                    unreadCount={isActive ? 0 : getUnreadCount(contact)}
                    isActive={isActive}
                    isOpening={openingMemberId === contact.memberId}
                    lastMessage={
                      contact.channelId
                        ? lastMessageList[contact.channelId]
                        : undefined
                    }
                    onSelect={() => void handleSelectContact(contact)}
                  />
                );
              })
            )}
          </div>
        </div>
        <div
          className={`${
            channelId ? '' : 'hidden md:block'
          } flex-1 bg-transparent overflow-hidden`}
        >
          {channelId ? (
            <Conversation
              key={channelId}
              channelId={channelId}
              loadingChannel={isLoading}
              isInquiryChat
              onBack={onBackToList}
              onMessageRecalled={handleRecallRefresh}
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              <Text center className="text-muted">
                {inquiryChatStrings.selectContact}
              </Text>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
